import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import {Box, Tooltip} from "@mui/material";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import React from "react";
import {useTranslation} from "react-i18next";

/**
 * The flags a question carries - Switched off, Required and Sensitive - in the one rendering every
 * staff-facing surface uses. The shape is fixed here and only the SIZE follows the surface. The markers
 * are glyphs, never chips: they are states of a question, while the type chip beside them is a category.
 */

/**
 * How large the pair draws.
 *
 * - `inline` sits after a title and takes its size FROM that title, so the asterisk is always a
 *   fifth larger than the words it qualifies - legible at a 15px heading, still proportionate at
 *   a 24px one.
 * - `standalone` sits in an icon strip or an actions row next to 28px chips, where there is no
 *   title to scale against.
 * - A number pins the asterisk to an explicit pixel size, for a title whose own size differs from
 *   the box the markers sit in (the runner's 24px heading beside a body-size Stack).
 */
export type SurveyMarkerSize = 'inline' | 'standalone' | number;

const STANDALONE_SIZE = 19;

const INLINE_ICON_SIZE = 15;

/** Required draws a fifth larger than its surroundings - an asterisk at body size is unreadable. */
const REQUIRED_SCALE = 1.2;

const requiredFontSize = (size: SurveyMarkerSize) => {
    if (size === 'inline') return `${REQUIRED_SCALE}em`;
    if (size === 'standalone') return STANDALONE_SIZE;
    return Math.round(size * REQUIRED_SCALE);
};

// The lock and the crossed-out eye: icon glyphs, drawn at the same size.
const iconFontSize = (size: SurveyMarkerSize) => {
    if (size === 'inline') return INLINE_ICON_SIZE;
    if (size === 'standalone') return STANDALONE_SIZE;
    return size;
};

/*
    The icon keeps MUI's default `aria-hidden`, and the accessible name lives on the wrapper
    instead. Passing `aria-label` to an aria-hidden <svg> - which is what every call site used to
    do - names nothing; `titleAccess` would name it but also fires a second, native tooltip on top
    of the MUI one.
*/
const MarkerBox = ({label, children, sx}: {
    label: string
    children: React.ReactNode
    sx?: object
}) => (
    <Tooltip title={label}>
        <Box
            component={'span'}
            role={'img'}
            aria-label={label}
            sx={{display: 'inline-flex', alignItems: 'center', flexShrink: 0, ...sx}}
        >
            {children}
        </Box>
    </Tooltip>
);

interface MarkerProps {
    size?: SurveyMarkerSize
    /** The caller's own `t` - lets the submission views pass their `SurveyAnswerContext` one. */
    t?: (key: string) => string
}

export function SurveyRequiredMarker({size = 'inline', t}: MarkerProps) {
    const {t: translate} = useTranslation();
    const label = (t ?? translate)('Required');

    return (
        <MarkerBox label={label} sx={{color: 'error.main'}}>
            <Box
                component={'span'}
                aria-hidden
                sx={{fontSize: requiredFontSize(size), fontWeight: 700, lineHeight: 1}}
            >
                *
            </Box>
        </MarkerBox>
    );
}

export function SurveySensitiveMarker({size = 'inline', t}: MarkerProps) {
    const {t: translate} = useTranslation();
    const label = (t ?? translate)('Sensitive');

    return (
        <MarkerBox label={label}>
            <LockOutlinedIcon sx={{fontSize: iconFontSize(size), color: 'warning.main'}}/>
        </MarkerBox>
    );
}

/** Guests never get the question. Grey, as it is a pause rather than a problem; the label says what it means. */
export function SurveySwitchedOffMarker({size = 'inline', t}: MarkerProps) {
    const {t: translate} = useTranslation();
    const label = (t ?? translate)('Switched off - guests don\'t see this question');

    return (
        <MarkerBox label={label}>
            <VisibilityOffOutlinedIcon sx={{fontSize: iconFontSize(size), color: 'text.secondary'}}/>
        </MarkerBox>
    );
}

interface Props extends MarkerProps {
    question: SurveyQuestion
    /**
     * Guest-facing surfaces never disclose that an answer is handled as sensitive - that is a
     * staff-side handling note, not something the runner tells the person answering. It is a flag
     * here rather than an omission at the call site so it cannot drift back in. Switched off is
     * staff-side too (guests never get such a question at all).
     */
    guest?: boolean
    /**
     * Space before each marker, as an `ml`. It ADDS to any gap the parent flex container already
     * supplies - so pass 0 inside a Stack or ListItem that sets its own `gap`, and leave the
     * default where the parent supplies none. Getting this wrong is invisible in isolation and
     * obvious next to the same markers on another surface. It applies to the FIRST marker too, so
     * a row that leads with the markers rather than a title wants 0 as well.
     */
    gap?: number
}

/**
 * Switched off, Required, Sensitive, in that order, everywhere: being off comes first because it outweighs
 * the other two. Renders nothing when no flag is set.
 */
export function SurveyQuestionMarkers({question, size = 'inline', guest, gap = 0.5, t}: Props) {
    const switchedOff = !!question.switchedOff && !guest;
    const required = !!question.isRequired;
    const sensitive = !!question.isSensitive && !guest;

    if (!switchedOff && !required && !sensitive) return null;

    return (
        <React.Fragment>
            {switchedOff && (
                <Box component={'span'} sx={{ml: gap, display: 'inline-flex', flexShrink: 0}}>
                    <SurveySwitchedOffMarker size={size} t={t}/>
                </Box>
            )}

            {required && (
                <Box component={'span'} sx={{ml: gap, display: 'inline-flex', flexShrink: 0}}>
                    <SurveyRequiredMarker size={size} t={t}/>
                </Box>
            )}

            {sensitive && (
                <Box component={'span'} sx={{ml: gap, display: 'inline-flex', flexShrink: 0}}>
                    <SurveySensitiveMarker size={size} t={t}/>
                </Box>
            )}
        </React.Fragment>
    );
}

export default SurveyQuestionMarkers;
